import { BlocoMotorDetalhado, TampaMotorDetalhada, ColetoresAdmissaoDetalhados, CarcacaCambioDetalhada } from '../anatomia/DetalhesMecanicos3D'
import { vetor } from '../../dominio/geometria/vetor3'
import {
  cofreMotor,
  yDaLongarina,
  zParaFogo,
  type PerfilVeiculo,
} from '../../dominio/didatico/perfilVeiculo'
import { Barra } from '../componentes/Barra'
import { MaterialPeca, Peca } from '../componentes/Peca'
import { CaixaArredondada } from '../componentes/CaixaArredondada'

/** Bloco simples de mecânica, com acabamento metálico fosco de peça fundida. */
function Bloco({
  id,
  posicao,
  args,
  rotacao,
}: {
  id: string
  posicao: [number, number, number]
  args: [number, number, number]
  rotacao?: [number, number, number]
}) {
  return (
    <mesh position={posicao} rotation={rotacao}>
      <CaixaArredondada args={args} />
      <MaterialPeca id={id} metalico={0.6} rugosidade={0.5} />
    </mesh>
  )
}

function Tubo({
  id,
  de,
  para,
  diametro,
}: {
  id: string
  de: ReturnType<typeof vetor>
  para: ReturnType<typeof vetor>
  diametro: number
}) {
  return <Barra id={id} de={de} para={para} largura={diametro} espessura={diametro} cilindrica />
}

/**
 * Conjunto motriz: motor, transmissão, admissão, direção e escapamento.
 *
 * Nada disto existia na maquete, e é o que o aluno encontra ao levantar o capô
 * numa ocorrência. Para o corte importa por dois motivos: são massas rígidas
 * que não se deformam junto com a chapa, e o escapamento chega quente ao ponto
 * de corte do assoalho.
 *
 * O desenho é esquemático de propósito. Ensina onde cada conjunto fica e o que
 * ocupa espaço, não como o motor funciona.
 */
export function ConjuntoMotriz({ perfil, detalhesAnatomia = false }: { perfil: PerfilVeiculo; detalhesAnatomia?: boolean }) {
  const cofre = cofreMotor(perfil)
  const yBase = yDaLongarina(perfil)
  const zCentroCofre = (cofre.zDianteiro + cofre.zTraseiro) / 2
  const yMotor = yBase + 0.3
  const traseiro = perfil.tracao !== 'dianteira'

  // FWD transversal: o motor fica atravessado e a transmissão sai pela lateral
  const xMotor = perfil.tracao === 'dianteira' ? 0.12 : 0
  const larguraMotor = perfil.tracao === 'dianteira' ? perfil.largura * 0.42 : 0.42
  const profundidadeMotor = perfil.tracao === 'dianteira' ? 0.42 : perfil.largura * 0.42

  return (
    <group>
      {/* ---------- motor ---------- */}
      <Peca id="motor" deslocamentoAfastado={[0, -0.15, 0.25]}>
        <Peca id="motor.bloco">
          <Bloco
            id="motor.bloco"
            posicao={[xMotor, yMotor, zCentroCofre]}
            args={[larguraMotor, 0.34, profundidadeMotor]}
          />
          {detalhesAnatomia && <group position={[xMotor, yMotor, zCentroCofre]}><BlocoMotorDetalhado largura={larguraMotor} profundidade={profundidadeMotor} /></group>}
        </Peca>

        <Peca id="motor.cabecote">
          <Bloco
            id="motor.cabecote"
            posicao={[xMotor, yMotor + 0.24, zCentroCofre - 0.02]}
            args={[larguraMotor * 0.92, 0.14, profundidadeMotor * 0.86]}
          />
          {detalhesAnatomia && <group position={[xMotor, yMotor + 0.24, zCentroCofre - 0.02]}><TampaMotorDetalhada largura={larguraMotor} profundidade={profundidadeMotor} /></group>}
        </Peca>

        <Peca id="motor.coletorAdmissao">
          <Bloco
            id="motor.coletorAdmissao"
            posicao={[xMotor, yMotor + 0.2, zCentroCofre - profundidadeMotor * 0.62]}
            args={[larguraMotor * 0.7, 0.16, 0.14]}
          />
          {detalhesAnatomia && <group position={[xMotor, yMotor + 0.2, zCentroCofre - profundidadeMotor * 0.62]}><ColetoresAdmissaoDetalhados largura={larguraMotor} profundidade={profundidadeMotor} /></group>}
        </Peca>

        {/* Coxins: é por eles que o motor se solta do berço no impacto. */}
        <Peca id="motor.coxim">
          {[1, -1].map((sinal) => (
            <Bloco
              key={sinal}
              id="motor.coxim"
              posicao={[xMotor + sinal * larguraMotor * 0.55, yMotor - 0.12, zCentroCofre]}
              args={[0.1, 0.12, 0.12]}
            />
          ))}
        </Peca>
      </Peca>

      {/* ---------- transmissão ---------- */}
      <Peca id="cambio" deslocamentoAfastado={[-0.6, 0.4, 0]}>
        <Bloco
          id="cambio"
          posicao={
            traseiro
              ? [0, yMotor - 0.06, zCentroCofre - profundidadeMotor * 0.9]
              : [xMotor - larguraMotor * 0.78, yMotor - 0.06, zCentroCofre]
          }
          args={traseiro ? [0.3, 0.28, 0.5] : [0.34, 0.3, 0.34]}
        />
        {detalhesAnatomia && <group position={traseiro ? [0, yMotor - 0.06, zCentroCofre - profundidadeMotor * 0.9] : [xMotor - larguraMotor * 0.78, yMotor - 0.06, zCentroCofre]}>
          <CarcacaCambioDetalhada largura={traseiro ? 0.3 : 0.34} altura={traseiro ? 0.28 : 0.3} profundidade={traseiro ? 0.5 : 0.34} />
        </group>}
      </Peca>

      {/* ---------- caixa de ar do filtro ---------- */}
      <Peca id="caixaFiltroAr" deslocamentoAfastado={[0, 0.6, 0.2]}>
        <Bloco
          id="caixaFiltroAr"
          posicao={[xMotor - larguraMotor * 0.5, yMotor + 0.3, cofre.zDianteiro - 0.16]}
          args={[0.26, 0.18, 0.24]}
        />
      </Peca>

      {/* ---------- direção ---------- */}
      {/* A cremalheira fica atrás do motor, na altura do eixo. O eixo
          intermediário sobe pelo corta-fogo até a coluna de direção: sem ele a
          coluna do habitáculo terminava no ar. */}
      <Peca id="caixaDirecao" deslocamentoAfastado={[0, -0.5, 0.3]}>
        <Bloco
          id="caixaDirecao"
          posicao={[0, yBase + 0.12, cofre.zTraseiro + 0.1]}
          args={[perfil.largura * 0.62, 0.1, 0.1]}
        />
        <Tubo
          id="caixaDirecao"
          de={vetor(0.1, yBase + 0.16, cofre.zTraseiro + 0.1)}
          para={vetor(0.18, perfil.cinturaY - 0.3, zParaFogo(perfil) - 0.12)}
          diametro={0.05}
        />
      </Peca>

      {/* ---------- semi-eixos ---------- */}
      <Peca id="semiEixos" deslocamentoAfastado={[0, -0.6, 0]}>
        {[1, -1].map((sinal) => (
          <Tubo
            key={sinal}
            id="semiEixos"
            de={vetor(
              traseiro ? sinal * 0.14 : xMotor - larguraMotor * 0.4 * sinal,
              yBase + 0.1,
              traseiro ? perfil.eixoTraseiroZ : zCentroCofre,
            )}
            para={vetor(
              sinal * (perfil.largura / 2 - perfil.larguraRoda),
              perfil.raioRoda,
              traseiro ? perfil.eixoTraseiroZ : perfil.eixoDianteiroZ,
            )}
            diametro={0.055}
          />
        ))}
      </Peca>

      {/* ---------- transmissão longitudinal, só quando há ---------- */}
      {traseiro && (
        <>
          <Peca id="cardan" deslocamentoAfastado={[0, -0.6, 0]}>
            <Tubo
              id="cardan"
              de={vetor(0, yBase + 0.06, cofre.zTraseiro - 0.3)}
              para={vetor(0, yBase + 0.06, perfil.eixoTraseiroZ)}
              diametro={0.07}
            />
          </Peca>
          <Peca id="diferencialTraseiro" deslocamentoAfastado={[0, -0.6, -0.2]}>
            <mesh position={[0, yBase + 0.06, perfil.eixoTraseiroZ]}>
              <sphereGeometry args={[0.16, 14, 12]} />
              <MaterialPeca id="diferencialTraseiro" metalico={0.6} rugosidade={0.5} />
            </mesh>
          </Peca>
        </>
      )}

      {/* ---------- escapamento ---------- */}
      {/* Corre pelo túnel central e chega quente ao ponto onde se corta o
          assoalho: é por isso que aparece inteiro, e não só o silencioso. */}
      <Peca id="escapamento" deslocamentoAfastado={[0, -0.7, 0]}>
        <Peca id="escapamento.coletor">
          <Tubo
            id="escapamento.coletor"
            de={vetor(xMotor, yMotor - 0.06, zCentroCofre + profundidadeMotor * 0.4)}
            para={vetor(0.06, yBase - 0.02, cofre.zTraseiro - 0.1)}
            diametro={0.06}
          />
        </Peca>

        <Peca id="escapamento.catalisador">
          <Bloco
            id="escapamento.catalisador"
            posicao={[0.06, yBase - 0.02, cofre.zTraseiro - 0.34]}
            args={[0.12, 0.12, 0.34]}
          />
        </Peca>

        <Peca id="escapamento.silencioso">
          <mesh
            position={[0.06, yBase - 0.02, perfil.traseiraZ + 0.38]}
            rotation={[Math.PI / 2, 0, 0]}
          >
            <cylinderGeometry args={[0.1, 0.1, 0.42, 14]} />
            <MaterialPeca id="escapamento.silencioso" metalico={0.6} rugosidade={0.5} />
          </mesh>
        </Peca>

        <Tubo
          id="escapamento"
          de={vetor(0.06, yBase - 0.02, cofre.zTraseiro - 0.5)}
          para={vetor(0.06, yBase - 0.02, perfil.traseiraZ + 0.58)}
          diametro={0.05}
        />
      </Peca>

      {/* ---------- cubos de roda ---------- */}
      <Peca id="cubosRoda" deslocamentoAfastado={[0, 0, 0]}>
        {[perfil.eixoDianteiroZ, perfil.eixoTraseiroZ].flatMap((z) =>
          [1, -1].map((sinal) => (
            <mesh
              key={`${z}:${sinal}`}
              position={[sinal * (perfil.largura / 2 - perfil.larguraRoda), perfil.raioRoda, z]}
              rotation={[0, 0, Math.PI / 2]}
            >
              <cylinderGeometry args={[0.1, 0.1, 0.07, 14]} />
              <MaterialPeca id="cubosRoda" metalico={0.7} rugosidade={0.4} />
            </mesh>
          )),
        )}
      </Peca>
    </group>
  )
}

